/**
 * Helper de captura visual que expande scrolls internos e desativa
 * paginação antes de chamar html2canvas, depois restaura tudo.
 *
 * Funciona em conjunto com:
 *  - ExportRenderContext (setRenderAll) → expande paginação no React.
 *  - body[data-export-mode="true"] → regras CSS globais que neutralizam
 *    max-height/overflow:auto em containers com `data-export-expand` ou
 *    no PainelTabela.
 */

import html2canvas from "html2canvas";
import { normalizarCoresParaCaptura } from "./normalizarCores";

const DEFAULT_OPTIONS = {
  scale: 2,
  useCORS: true,
  backgroundColor: "#ffffff",
  logging: false,
};

function nextFrame() {
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Captura um elemento DOM com scroll + paginação expandidos.
 *
 * @param {HTMLElement} element  elemento a capturar
 * @param {Object} opts
 * @param {() => void|Promise<void>} [opts.beforeCapture]  ex: setRenderAll(true)
 * @param {() => void|Promise<void>} [opts.afterCapture]   ex: setRenderAll(false)
 * @param {number} [opts.scale=2]
 * @param {string} [opts.backgroundColor="#ffffff"]
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function captureWithExpand(element, opts = {}) {
  if (!element) throw new Error("captureWithExpand: elemento ausente");

  const {
    beforeCapture,
    afterCapture,
    scale = DEFAULT_OPTIONS.scale,
    backgroundColor = DEFAULT_OPTIONS.backgroundColor,
    // Largura que o elemento tinha NA TELA, medida antes de qualquer regra de
    // export-mode entrar. Sem ela a imagem sai esticada: as regras de captura
    // zeram o padding da sidebar e o max-width do container, o conteudo reflui
    // mais largo e as colunas flexiveis da tabela absorvem toda a sobra
    // (medido: tabela 969 -> 1217px, Cliente/Ambiente 200 -> 324px cada).
    larguraDeTela,
  } = opts;

  // Sinaliza ao React e ao CSS global para expandir o conteúdo.
  if (beforeCapture) await beforeCapture();
  const previousBodyAttr = document.body.getAttribute("data-export-mode");
  document.body.setAttribute("data-export-mode", "true");

  const larguraInline = element.style.width;
  const maxWidthInline = element.style.maxWidth;
  if (larguraDeTela) {
    element.style.width = `${larguraDeTela}px`;
    element.style.maxWidth = `${larguraDeTela}px`;
  }

  try {
    // Aguarda o React aplicar setRenderAll e o navegador reaplicar o CSS.
    // 4 RAFs + 80ms cobrem casos onde a re-render envolve listas grandes.
    await nextFrame();
    await nextFrame();
    await sleep(80);

    // Mede DEPOIS do CSS ter expandido tudo. scrollWidth/Height passam a
    // refletir o conteudo completo (sem scroll interno cortando nada).
    const fullWidth = Math.max(
      element.scrollWidth,
      element.offsetWidth,
      element.getBoundingClientRect().width
    );
    const fullHeight = Math.max(
      element.scrollHeight,
      element.offsetHeight,
      element.getBoundingClientRect().height
    );

    // Cores modernas (color-mix -> color(srgb ...)) viram rgba() legado so
    // durante a captura — o html2canvas 1.4.1 nao as parseia e aborta.
    const restaurarCores = normalizarCoresParaCaptura(element);

    try {
      // windowWidth/Height = viewport REAL. Passar o tamanho do elemento aqui
      // (comportamento anterior) fazia o html2canvas re-layoutar o clone num
      // pseudo-viewport do tamanho da secao — cada secao encolhia de um jeito
      // e a imagem final saia com faixa de fundo sobrando a direita e bordas
      // desalinhadas entre cards e tabela (bug 2026-07-06).
      return await html2canvas(element, {
        ...DEFAULT_OPTIONS,
        scale,
        backgroundColor,
        width: fullWidth,
        height: fullHeight,
        windowWidth: document.documentElement.clientWidth,
        windowHeight: Math.max(window.innerHeight, fullHeight),
        scrollX: 0,
        scrollY: 0,
      });
    } finally {
      restaurarCores();
    }
  } finally {
    if (larguraDeTela) {
      element.style.width = larguraInline;
      element.style.maxWidth = maxWidthInline;
    }
    if (previousBodyAttr) {
      document.body.setAttribute("data-export-mode", previousBodyAttr);
    } else {
      document.body.removeAttribute("data-export-mode");
    }
    if (afterCapture) await afterCapture();
  }
}

const ACCENT_HEX = "#ec3223";
const TEXT_PRIMARY_HEX = "#16181e";
const TEXT_SECONDARY_HEX = "#686d76";
// Fonte do cabeçalho desenhado no canvas — espelha --font-body do DS.
const FONT_FAMILY_PADRAO = '"Sora", "Segoe UI", system-ui, sans-serif';

/**
 * Recebe vários canvases já capturados e produz UM canvas único,
 * empilhando verticalmente com padding. Retorna o canvas resultante.
 *
 * @param {HTMLCanvasElement[]} canvases
 * @param {Object} [opts]
 * @param {number} [opts.padding=40]            padding em pixels (DPR-aware)
 * @param {number} [opts.spacing=24]            espaço entre seções
 * @param {string} [opts.backgroundColor="#ffffff"]
 * @param {string} [opts.title]                 título exibido no topo da imagem
 * @param {string} [opts.subtitle]              subtítulo opcional logo abaixo
 */
export function combineCanvasesVertical(canvases, opts = {}) {
  const list = canvases.filter(Boolean);
  if (list.length === 0) {
    throw new Error("combineCanvasesVertical: nenhum canvas para combinar");
  }
  const {
    padding = 40,
    spacing = 24,
    backgroundColor = "#ffffff",
    // Cores do cabeçalho/fundo — sobrescritas com o tema atual do usuário
    // (light/dark) pela camada de exportação. Defaults = tema claro, para
    // não mudar o comportamento de quem não passa tema (ex.: agendador).
    titleColor = TEXT_PRIMARY_HEX,
    subtitleColor = TEXT_SECONDARY_HEX,
    accentColor = ACCENT_HEX,
    fontFamily = FONT_FAMILY_PADRAO,
    title,
    subtitle,
  } = opts;

  const width = Math.max(...list.map((c) => c.width));

  // Header: reservamos altura proporcional à largura do conteúdo (para
  // que título fique grande em capturas em alta DPI e legível em pequenas).
  const hasHeader = Boolean(title);
  const headerScale = Math.max(1, Math.min(width / 1200, 2.5));
  const titleFontPx = Math.round(34 * headerScale);
  const subtitleFontPx = Math.round(16 * headerScale);
  const accentBarPx = Math.max(2, Math.round(3 * headerScale));
  const headerInnerGap = Math.round(10 * headerScale);
  const headerHeight = hasHeader
    ? titleFontPx +
      (subtitle ? subtitleFontPx + headerInnerGap : 0) +
      accentBarPx +
      Math.round(24 * headerScale)
    : 0;

  const height =
    list.reduce((acc, c) => acc + c.height, 0) +
    spacing * (list.length - 1) +
    padding * 2 +
    headerHeight;

  const out = document.createElement("canvas");
  out.width = width + padding * 2;
  out.height = height;
  const ctx = out.getContext("2d");
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, out.width, out.height);

  let y = padding;

  if (hasHeader) {
    // Título (peso 700)
    ctx.fillStyle = titleColor;
    ctx.font = `700 ${titleFontPx}px ${fontFamily}`;
    ctx.textBaseline = "top";
    ctx.fillText(title, padding, y);
    y += titleFontPx;

    if (subtitle) {
      y += Math.round(headerInnerGap * 0.4);
      ctx.fillStyle = subtitleColor;
      ctx.font = `400 ${subtitleFontPx}px ${fontFamily}`;
      ctx.fillText(subtitle, padding, y);
      y += subtitleFontPx + Math.round(headerInnerGap * 0.6);
    } else {
      y += Math.round(headerInnerGap * 0.6);
    }

    // Linha accent fina
    ctx.fillStyle = accentColor;
    ctx.fillRect(padding, y, width, accentBarPx);
    y += accentBarPx + Math.round(24 * headerScale);
  }

  for (const c of list) {
    // centraliza horizontalmente se o canvas atual for mais estreito.
    const x = padding + Math.max(0, Math.round((width - c.width) / 2));
    ctx.drawImage(c, x, y);
    y += c.height + spacing;
  }
  return out;
}

/**
 * Limite defensivo de altura para evitar imagens monstruosas.
 */
export const MAX_IMAGE_HEIGHT_PX = 16000;

/** Teto de ÁREA de canvas do Chrome (16384² px). Acima disso ele desiste. */
export const MAX_IMAGE_AREA_PX = 268435456;

/**
 * Diz por que um canvas é inviável — ou `null` se está de bom tamanho.
 *
 * Sem este gate, um painel de fluxo com ~800 linhas e `renderAll` ligado
 * produzia um canvas de ~2100×40000. O Chrome não aloca, o `drawImage`
 * no-opera SEM lançar, e sai um retângulo de fundo sólido: `toBlob` devolve
 * um blob válido, o arquivo é gravado e o histórico registra "ok". O gestor
 * abria um JPG em branco achando que era problema do visualizador.
 */
export function motivoImagemInviavel(canvas) {
  if (!canvas) return "Captura não produziu imagem.";
  const { width, height } = canvas;
  if (!width || !height) {
    return "Captura resultou numa imagem de tamanho zero.";
  }
  if (height > MAX_IMAGE_HEIGHT_PX) {
    return (
      `Imagem de ${height}px de altura excede o limite de ` +
      `${MAX_IMAGE_HEIGHT_PX}px — use PDF ou Excel para painéis longos.`
    );
  }
  if (width * height > MAX_IMAGE_AREA_PX) {
    return (
      `Imagem de ${width}×${height}px passa do teto de área do navegador — ` +
      `o arquivo sairia em branco. Use PDF ou Excel para painéis longos.`
    );
  }
  return null;
}
